/* ==========================================================================
   Royal Care Professional — before / after story

   Three levels, each driven by a different gesture:

     Heading — SCROLL, while the stage is pinned.
     Tab     — TAP.
     Product — TAP.

   Scroll never reaches past the heading, and that is the whole reason the
   three coexist. A visitor who taps a tab or a product keeps it for as long as
   its heading is the one on screen, because scrolling within a heading
   resolves to the same index and selectGroup returns early. Each level also
   remembers what it was left on, so coming back to a heading restores its tab
   and that tab's product rather than resetting to the first of each.

   Heading buttons scroll the page to where their heading lives rather than
   setting the index, since while pinned the scroll position is the truth and
   setting it directly would only flicker back as the smooth scroll crossed the
   midpoint. Tab and product taps never scroll: they belong to the heading the
   visitor is already looking at.

   The scroll handler is passive and coalesced into requestAnimationFrame, and
   it does one getBoundingClientRect per frame at most. Below the pinning
   breakpoint it does not run at all.

   The comparison slider itself — drag, touch, arrow keys, the reveal hint — is
   the theme's, in comparison-slider.js.
   ========================================================================== */

(function () {
  'use strict';

  if (window.RCBeforeAfter) {
    window.RCBeforeAfter.init();
    return;
  }

  var PINNED = '(min-width: 990px)';

  function Story(root) {
    this.root = root;
    this.track = root.querySelector('[data-rc-ba-track]');
    this.stage = root.querySelector('[data-rc-ba-stage]');
    this.sliders = root.querySelectorAll('comparison-slider-component');
    this.pairs = root.querySelectorAll('[data-rc-ba-pair]');
    this.groups = root.querySelectorAll('[data-rc-ba-group]');
    this.groupPanels = root.querySelectorAll('[data-rc-ba-group-panel]');
    this.tabs = root.querySelectorAll('[data-rc-ba-tab]');

    this.count = this.groups.length;
    this.frame = 0;
    this.pinned = false;

    // Seeded from the markup so the first scroll frame does not redo work the
    // server already did.
    var open = root.querySelector('.rc-ba__title.is-active [data-rc-ba-group]');
    this.group = open ? open.getAttribute('data-rc-ba-group') : null;

    var tab = root.querySelector('[data-rc-ba-group-panel].is-active [data-rc-ba-tab].is-active');
    this.tab = tab ? tab.getAttribute('data-rc-ba-tab') : null;

    this.onScroll = this.onScroll.bind(this);
    this.measure = this.measure.bind(this);
  }

  /* ---- 1. the reveal ---- */

  /*
   * Mirror one slider's position onto the others. Each product owns its own
   * component, so without this the reveal position would reset every time the
   * selection changed. setValue is the component's own API; the fallback
   * covers the window between markup and hydration, and any browser where the
   * module never ran.
   */
  Story.prototype.bindSliders = function () {
    var sliders = this.sliders;
    if (sliders.length < 2) return;

    Array.prototype.forEach.call(sliders, function (slider) {
      slider.addEventListener('input', function (event) {
        var value = event.target && event.target.value;
        if (value == null) return;

        Array.prototype.forEach.call(sliders, function (other) {
          if (other === slider) return;

          if (typeof other.setValue === 'function') {
            other.setValue(Number(value));
            return;
          }

          var input = other.querySelector('input[type="range"]');
          var wrapper = other.querySelector('[ref="mediaWrapper"]');
          if (input) input.value = value;
          if (wrapper) wrapper.style.setProperty('--compare', String(value));
        });
      });
    });
  };

  /* ---- 2. the viewer ---- */

  Story.prototype.showPair = function (id) {
    if (!id) return;

    Array.prototype.forEach.call(this.pairs, function (pair) {
      var on = pair.getAttribute('data-rc-ba-pair') === id;
      pair.classList.toggle('is-active', on);
      if (on) pair.removeAttribute('aria-hidden');
      else pair.setAttribute('aria-hidden', 'true');
    });
  };

  /* ---- 3. the products ---- */

  Story.prototype.selectItem = function (button) {
    if (!button) return;

    var panel = button.closest('[data-rc-ba-panel]');
    if (panel) {
      Array.prototype.forEach.call(panel.querySelectorAll('[data-rc-ba-item]'), function (other) {
        var on = other === button;
        other.setAttribute('aria-pressed', String(on));
        var card = other.closest('.rc-ba__card');
        if (card) card.classList.toggle('is-active', on);
      });
    }

    this.showPair(button.getAttribute('data-pair'));
  };

  /* ---- 4. the tabs ---- */

  Story.prototype.selectTab = function (id) {
    if (id == null || id === this.tab) return;

    var target = this.root.querySelector('[data-rc-ba-tab="' + id + '"]');
    if (!target) return;

    this.tab = id;

    var self = this;

    /*
     * Only this heading's own strip is touched. Clearing every tab in the
     * section would wipe the other headings' choices, and scrolling back to
     * one would then drop the visitor on its first tab instead of the tab
     * they picked. The headings that are off screen stay hidden either way —
     * the group they sit in is.
     */
    var scope = target.closest('[data-rc-ba-group-panel]') || this.root;
    var tabs = scope.querySelectorAll('[data-rc-ba-tab]');
    var panels = scope.querySelectorAll('[data-rc-ba-panel]');

    Array.prototype.forEach.call(tabs, function (tab) {
      var on = tab.getAttribute('data-rc-ba-tab') === id;
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-selected', String(on));
    });

    Array.prototype.forEach.call(panels, function (panel) {
      var on = panel.getAttribute('data-rc-ba-panel') === id;
      panel.classList.toggle('is-active', on);
      if (on) panel.removeAttribute('aria-hidden');
      else panel.setAttribute('aria-hidden', 'true');

      if (!on) return;

      /*
       * Hand the viewer back to whichever product this list was left on, so a
       * visitor who compares two ranges and returns does not lose their place.
       * A freshly opened list falls back to its first product.
       */
      var current = panel.querySelector('[data-rc-ba-item][aria-pressed="true"]');
      self.selectItem(current || panel.querySelector('[data-rc-ba-item]'));
    });
  };

  /* ---- 5. the headings ---- */

  Story.prototype.selectGroup = function (id) {
    // Scrolling within one heading resolves to the same id frame after frame.
    // Returning here is what leaves a tapped tab and product alone.
    if (id == null || id === this.group) return;
    this.group = id;

    var self = this;

    Array.prototype.forEach.call(this.groups, function (button) {
      var on = button.getAttribute('data-rc-ba-group') === id;
      button.setAttribute('aria-current', String(on));
      var row = button.closest('.rc-ba__title');
      if (row) row.classList.toggle('is-active', on);
    });

    Array.prototype.forEach.call(this.groupPanels, function (panel) {
      var on = panel.getAttribute('data-rc-ba-group-panel') === id;
      panel.classList.toggle('is-active', on);
      if (on) panel.removeAttribute('aria-hidden');
      else panel.setAttribute('aria-hidden', 'true');

      if (!on) return;

      // Same courtesy one level up: the heading reopens on the tab it was
      // left on, which in turn reopens on its own product.
      var current = panel.querySelector('[data-rc-ba-tab].is-active');
      var next = current || panel.querySelector('[data-rc-ba-tab]');
      if (next) self.selectTab(next.getAttribute('data-rc-ba-tab'));
    });
  };

  /* ---- 6. the pinned stage ---- */

  Story.prototype.measure = function () {
    this.frame = 0;
    if (!this.track || !this.stage || this.count < 2) return;

    var runway = this.track.offsetHeight - this.stage.offsetHeight;
    if (runway <= 0) return;

    var travelled = -this.track.getBoundingClientRect().top;
    var progress = travelled / runway;
    if (progress < 0) progress = 0;
    if (progress > 1) progress = 1;

    // Round so each heading owns an equal share of the runway.
    var button = this.groups[Math.round(progress * (this.count - 1))];
    if (button) this.selectGroup(button.getAttribute('data-rc-ba-group'));
  };

  Story.prototype.onScroll = function () {
    if (this.frame) return;
    this.frame = requestAnimationFrame(this.measure);
  };

  Story.prototype.pin = function () {
    if (this.pinned) return;
    this.pinned = true;

    /*
     * Horizon scrolls .page-wrapper rather than the window from 990px up, so a
     * listener on window would never fire. Scroll events do not bubble but do
     * travel the capture phase, so one capturing listener on document catches
     * whichever element is actually scrolling.
     */
    document.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.measure();
  };

  Story.prototype.unpin = function () {
    if (!this.pinned) return;
    this.pinned = false;
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    window.removeEventListener('resize', this.onScroll);
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
    // The heading on screen stays on screen: below the breakpoint the visitor
    // taps for it, and resetting would throw away a choice they just made.
  };

  Story.prototype.goToGroup = function (i) {
    var button = this.groups[i];
    if (!button) return;

    if (!this.pinned) {
      this.selectGroup(button.getAttribute('data-rc-ba-group'));
      return;
    }

    var runway = this.track.offsetHeight - this.stage.offsetHeight;
    var share = this.count > 1 ? i / (this.count - 1) : 0;
    var top = this.track.getBoundingClientRect().top + runway * share;

    var scroller = this.track.closest('.page-wrapper') || document.scrollingElement;
    scroller.scrollBy({ top: top, behavior: 'smooth' });
  };

  /* ---- wiring ---- */

  Story.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (event) {
      var group = event.target.closest('[data-rc-ba-group]');
      if (group && self.root.contains(group)) {
        self.goToGroup(Array.prototype.indexOf.call(self.groups, group));
        return;
      }

      var tab = event.target.closest('[data-rc-ba-tab]');
      if (tab && self.root.contains(tab)) {
        self.selectTab(tab.getAttribute('data-rc-ba-tab'));
        return;
      }

      var item = event.target.closest('[data-rc-ba-item]');
      if (item && self.root.contains(item)) self.selectItem(item);
    });

    // Left/right along the tab strip, the arrangement a tablist is expected to
    // have. The tabs are buttons, so Enter and Space already work. Only the
    // tabs of the heading on screen are reachable, so the walk skips the rest.
    Array.prototype.forEach.call(this.tabs, function (tab) {
      tab.addEventListener('keydown', function (event) {
        var step = 0;
        if (event.key === 'ArrowRight') step = 1;
        else if (event.key === 'ArrowLeft') step = -1;
        else return;

        var strip = tab.closest('.rc-ba__tabs');
        if (!strip) return;

        var siblings = strip.querySelectorAll('[data-rc-ba-tab]');
        var here = Array.prototype.indexOf.call(siblings, tab);
        var next = siblings[(here + step + siblings.length) % siblings.length];
        if (!next) return;

        event.preventDefault();
        next.focus();
        self.selectTab(next.getAttribute('data-rc-ba-tab'));
      });
    });
  };

  Story.prototype.start = function () {
    this.bindSliders();
    this.bind();

    if (this.root.getAttribute('data-pin') !== 'true' || this.count < 2) return;

    var mq = window.matchMedia(PINNED);
    var self = this;

    var pick = function () {
      if (mq.matches) self.pin();
      else self.unpin();
    };

    pick();
    mq.addEventListener ? mq.addEventListener('change', pick) : mq.addListener(pick);
  };

  var RCBeforeAfter = {
    init: function (root) {
      var scope = root || document;
      Array.prototype.forEach.call(scope.querySelectorAll('[data-rc-ba]'), function (el) {
        if (el.dataset.rcBaReady === 'true') return;
        el.dataset.rcBaReady = 'true';
        new Story(el).start();
      });
    }
  };

  window.RCBeforeAfter = RCBeforeAfter;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      RCBeforeAfter.init();
    });
  } else {
    RCBeforeAfter.init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    RCBeforeAfter.init(event.target);
  });
})();
